import React, { useState, useEffect, useRef } from 'react';
import { VconSummary, VconParty, VconLifecycleEvent, ConsentDetail, VconHolder, RedactResult, RedactMode, ThreadMember, ThreadSuggestion, ThreadConsent, ApiResponse, VconDialogText, DialogSegment } from '../types';
import { vconService, lifecycleService, consentService, threadService, transcriptionService, CONSENT_PROOF_TYPES, CONSENT_LAWFUL_BASES, LAWFUL_BASIS_LABELS, SearchParams } from '../services/api';
import { formatDateTime } from '../utils/datetime';
import ScopeFilter, { loadScopeTree } from './ScopeFilter';
import { LIFECYCLE_EVENT_LABELS, lifecycleActorLabel } from './logs/LifecycleLedgerTable';
import { sessionLetters, SessionLegend } from './ThreadsPage';

// Playback state: the audio is decoded out of the vCon document itself
// (dialog[0].body, base64url), not streamed from the WAV file.
interface VconPlayback {
  vcon: VconSummary;
  audioUrl: string | null;
  loading: boolean;
  error: string | null;
}

const base64urlToBytes = (b64url: string): Uint8Array => {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

// initialUuid opens the table already filtered to one conversation. It exists
// for the Ask tab's citations: clicking one should land on the record itself,
// not on a search box the reader has to re-type the uuid into.
interface VconsTableProps {
  initialUuid?: string;
}

// --- Conversation view of a timed transcript -------------------------------

// One colour per participant, so a speaker can be followed down the page.
// Keyed on their audio channel — one per participant — not on turn order, so
// a speaker keeps the same colour all the way through.
const SPEAKER_COLORS = ['#2a78d6', '#C8862F', '#2e9e6b', '#9b59b6', '#d35454', '#5b6b7f'];

// A speaker's consecutive utterances are one turn unless they paused this
// long — then a new timestamp is worth showing.
const TURN_GAP_SECONDS = 4;

interface ConversationTurn {
  key: number;
  speaker: string;
  colour: number;
  start: number;
  end: number | null;
  text: string;
  confidence: number | null;
  overlaps: boolean;
}

const formatOffset = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// The transcript as a conversation: every party's timed utterances in the
// order they were spoken, a speaker's run of lines joined into one turn.
// Null when the transcript carries no timing — made before utterances were
// timed — so the caller falls back to the plain per-entry list.
function conversationTurns(segments: DialogSegment[]): ConversationTurn[] | null {
  const timed = segments.filter(s => s.is_transcript && s.text && s.generated_by?.offset != null);
  if (timed.length === 0) return null;
  timed.sort((a, b) => (a.generated_by!.offset! - b.generated_by!.offset!) || (a.index - b.index));

  const turns: ConversationTurn[] = [];
  const confidences: number[][] = [];
  // When each speaker last stopped, to tell a reply from an interruption.
  const lastEndBySpeaker = new Map<string, number>();
  for (const seg of timed) {
    const g = seg.generated_by!;
    const start = g.offset!;
    const end = g.end ?? null;
    const speaker = seg.speaker || 'Unattributed';
    const last = turns[turns.length - 1];
    if (last && last.speaker === speaker
        && start - (last.end ?? last.start) <= TURN_GAP_SECONDS) {
      last.text += ' ' + seg.text;
      if (end != null) last.end = Math.max(last.end ?? end, end);
      if (g.confidence != null) confidences[confidences.length - 1].push(g.confidence);
    } else {
      turns.push({
        key: seg.index,
        speaker,
        colour: g.channel != null ? g.channel - 1 : turns.length,
        start,
        end,
        text: seg.text!,
        confidence: null,
        overlaps: Array.from(lastEndBySpeaker.entries())
          .some(([who, stopped]) => who !== speaker && start < stopped),
      });
      confidences.push(g.confidence != null ? [g.confidence] : []);
    }
    if (end != null) lastEndBySpeaker.set(speaker, Math.max(lastEndBySpeaker.get(speaker) ?? end, end));
  }
  turns.forEach((t, i) => {
    const c = confidences[i];
    t.confidence = c.length ? Math.min(...c) : null;
  });
  return turns;
}

const VconsTable: React.FC<VconsTableProps> = ({ initialUuid }) => {
  const [vcons, setVcons] = useState<VconSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useState<SearchParams>(
    { page: 1, limit: 25, ...(initialUuid ? { uuid: initialUuid } : {}) });

  // Re-filter when a DIFFERENT citation is clicked while this tab is already
  // open. Without this the component is already mounted, the initial state has
  // been consumed, and the second click appears to do nothing.
  useEffect(() => {
    if (initialUuid) {
      setSearchParams(prev => ({ ...prev, page: 1, uuid: initialUuid }));
    }
  }, [initialUuid]);
  // Controlled filter inputs, same reasoning as SessionsTable: every fetch
  // swaps the render tree for the "Loading..." div, which would reset
  // uncontrolled inputs to empty.
  const [searchText, setSearchText] = useState('');
  const [startFromText, setStartFromText] = useState('');
  const [startToText, setStartToText] = useState('');
  const [playback, setPlayback] = useState<VconPlayback | null>(null);
  // Delete is two-step: the row button only arms this; the actual delete
  // happens from the confirmation modal.
  const [deleteTarget, setDeleteTarget] = useState<VconSummary | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  // Content search is kept separate from the metadata box on purpose: they
  // answer different questions ("which call was this?" vs "which calls talked
  // about X?"), match differently — substring vs whole word — and only one of
  // them re-orders the results by relevance.
  const [contentText, setContentText] = useState('');
  // Which vCon is being transcribed right now, and what the last attempt said.
  // Keyed by uuid rather than a single flag so the row that was clicked is the
  // row that shows progress, even if the user starts another.
  const [transcribing, setTranscribing] = useState<string | null>(null);
  const [transcribeResult, setTranscribeResult] = useState<{ uuid: string; text: string; ok: boolean } | null>(null);
  // The Transcript popup: what was said, without the document wrapper.
  const [textVcon, setTextVcon] = useState<VconSummary | null>(null);
  const [textDoc, setTextDoc] = useState<VconDialogText | null>(null);
  const [textLoading, setTextLoading] = useState(false);
  const [textError, setTextError] = useState<string | null>(null);
  // Whether the CURRENT results came from a content search — read from the
  // applied params, not the input box, so the extra column and the snippets
  // appear with the results rather than as soon as someone starts typing.
  const contentSearchActive = !!searchParams.content;

  // Uploads write into the store, so they are refused while a read-only
  // territory is selected — the research corpus is indexed in place and its
  // licence forbids adding to it. Read from the same cached scope tree the
  // filter uses rather than fetching it a second time.
  const [readOnlyTerritories, setReadOnlyTerritories] = useState<string[]>([]);
  useEffect(() => {
    loadScopeTree()
      .then(t => setReadOnlyTerritories(t.territories.filter(x => x.is_readonly).map(x => x.name)))
      .catch(() => {});
  }, []);
  const readOnlyScope = !!searchParams.territory
    && readOnlyTerritories.includes(searchParams.territory);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadNotice, setUploadNotice] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Per-vCon lifecycle timeline modal
  const [lifecycleVcon, setLifecycleVcon] = useState<VconSummary | null>(null);
  const [lifecycleEvents, setLifecycleEvents] = useState<VconLifecycleEvent[] | null>(null);
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);
  // View-document popup: abbreviated by default (media truncated
  // server-side); full document on demand.
  const [viewVcon, setViewVcon] = useState<VconSummary | null>(null);
  const [viewDoc, setViewDoc] = useState<any | null>(null);
  const [viewAbbreviated, setViewAbbreviated] = useState(true);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);
  // Consent modal: fetched detail plus the per-party checkbox draft
  const [consentVcon, setConsentVcon] = useState<VconSummary | null>(null);
  const [consentDetail, setConsentDetail] = useState<ConsentDetail | null>(null);
  const [consentDraft, setConsentDraft] = useState<Record<number, Set<string>>>({});
  const [consentBusy, setConsentBusy] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  // Proof-of-acquisition attached to grants made from this modal
  const [consentProofType, setConsentProofType] = useState('');
  const [consentProofRef, setConsentProofRef] = useState('');
  // Lawful basis for grants made from this modal. Empty means "leave this
  // party on whatever it already rests on" — the safe default, since a
  // console grant is rarely the thing that establishes the grounds.
  const [consentBasis, setConsentBasis] = useState('');
  // Copies & notifications (revocation cascade) shown in the consent modal
  const [consentHolders, setConsentHolders] = useState<VconHolder[] | null>(null);

  // Threading: which interaction this conversation belongs to, and what else
  // might belong with it.
  const [threadVcon, setThreadVcon] = useState<VconSummary | null>(null);
  const [threads, setThreads] = useState<{ thread_id: string; members: ThreadMember[] }[]>([]);
  const [threadSuggestions, setThreadSuggestions] = useState<ThreadSuggestion[] | null>(null);
  const [threadPicked, setThreadPicked] = useState<Set<string>>(new Set());
  const [threadIdInput, setThreadIdInput] = useState('');
  const [threadWindow, setThreadWindow] = useState(7);
  const [threadBusy, setThreadBusy] = useState(false);
  const [threadError, setThreadError] = useState<string | null>(null);
  // Thread-wide consent is asked for, never volunteered: it is the most
  // restrictive member's answer by construction, so showing it unprompted
  // would invite reading it as the verdict for each conversation.
  const [threadConsent, setThreadConsent] = useState<Record<string, ThreadConsent>>({});

  // Redact is two-step like Delete: the row button arms this modal, where
  // the operator picks the party and confirms.
  const [redactTarget, setRedactTarget] = useState<VconSummary | null>(null);
  const [redactParty, setRedactParty] = useState<number>(0);
  // Defaults to the reversible one. Erase is the answer to a right-to-erasure
  // request and nothing else, so it should be chosen, not arrived at.
  const [redactMode, setRedactMode] = useState<RedactMode>('derive');
  const [redactBusy, setRedactBusy] = useState(false);
  const [redactError, setRedactError] = useState<string | null>(null);
  const [redactResult, setRedactResult] = useState<RedactResult | null>(null);
  // What the Transcribe control may offer, and whether there is a nightly run
  // to defer to. Sent with the listing rather than fetched separately — the
  // answer belongs to the deployment, not to a row.
  const [transcription, setTranscription] = useState<ApiResponse<VconSummary>['transcription']>();
  // Transcribing is a two-step like Redact: the row button arms this dialog,
  // where the language is chosen and the run confirmed. The choice lives here
  // rather than in the row because it is the exception, not a decision to be
  // put in front of someone on every line of the table.
  const [transcribeTarget, setTranscribeTarget] = useState<VconSummary | null>(null);
  // '' = whatever the domain is set to, which is right for almost every call.
  const [transcribeLanguage, setTranscribeLanguage] = useState('');
  const [transcribeError, setTranscribeError] = useState<string | null>(null);

  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 0,
    total_count: 0,
    limit: 25,
    has_next: false,
    has_prev: false
  });

  const fetchVcons = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: ApiResponse<VconSummary> = await vconService.list(searchParams);
      setVcons(response.vcons || []);
      setTranscription(response.transcription);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to fetch vCons');
      console.error('Error fetching vCons:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVcons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Parses a 24-hour "YYYY-MM-DD HH:MM[:SS]" filter value (browser local
  // time) into the UTC "YYYY-MM-DD HH:MM:SS" form vcons.php compares
  // against created_at — identical to the Recording Sessions filters.
  const toUtcSqlDatetime = (local: string): string | undefined => {
    const m = local.trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!m) return undefined;
    const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
    if (isNaN(d.getTime())) return undefined;
    return d.toISOString().slice(0, 19).replace('T', ' ');
  };

  // Arm the dialog. The language resets to the domain default each time rather
  // than remembering the last choice: the previous call's language is not
  // evidence about this one, and a remembered override is one nobody sees.
  const openTranscribe = (vcon: VconSummary) => {
    setTranscribeLanguage('');
    setTranscribeError(null);
    setTranscribeResult(null);
    setTranscribeTarget(vcon);
  };

  // What this recording would be recognised as with nothing chosen — the
  // domain's setting, named rather than described.
  const domainDefaultFor = (vcon: VconSummary) =>
    transcription?.domain_defaults?.find(
      d => d.territory === vcon.territory && d.domain === vcon.domain);

  // Transcribe one recording on demand. The nightly job is the normal path;
  // this is for when someone is looking at a specific call and wants it now.
  const handleTranscribe = async (vcon: VconSummary) => {
    if (!vcon.uuid) return;
    setTranscribing(vcon.uuid);
    setTranscribeResult(null);
    setTranscribeError(null);
    try {
      const r = await transcriptionService.transcribe(vcon.uuid, transcribeLanguage || undefined);
      const ok = r.status === 'done';
      // Always say which language it was recognised as. A low confidence score
      // means "the engine could not make the audio out"; the same score with
      // "as en-US" beside it usually means it was asked the wrong question.
      const asLanguage = r.language ? ` as ${r.language}` : '';
      const text =
        r.status === 'done'
          ? `Transcribed${asLanguage}: ${r.segments} segment(s), ${r.chars} characters`
            + (r.confidence != null ? `, confidence ${(r.confidence * 100).toFixed(0)}%` : '')
            + '. It is searchable now.'
            + (r.confidence != null && r.confidence < 0.5
              ? ' Low confidence — if the call was in another language, pick it and transcribe again.'
              : '')
          : r.message || r.error || `Finished with status "${r.status}".`;
      setTranscribeResult({ uuid: vcon.uuid, text, ok });
      // The outcome belongs on the row, where the eye already is — so the
      // dialog closes on anything that reached the engine, including "no
      // speech" and "no consent", which are answers rather than errors.
      setTranscribeTarget(null);
      // Refresh so the row picks up its new transcript badge and consent state.
      fetchVcons();
    } catch (err: any) {
      // A request that never got an answer keeps the dialog open, with the
      // reason beside the button that would try again.
      setTranscribeError(
        err.response?.data?.error || err.response?.data?.message || 'Transcription failed.');
    } finally {
      setTranscribing(null);
    }
  };

  // The Transcript cell: state and the action that state allows, together.
  //
  // These used to be in two columns — a badge beside the consent status and
  // two buttons among nine others — which made the state a tooltip-sized
  // hover target and hid the connection between "not transcribed" and the
  // button that would fix it. One cell, one story per row.
  //
  // The confidence is rendered as text rather than left in a tooltip: on 8 kHz
  // telephony it is the difference between a quotation and a guess, and it
  // should not require steady aim to read.
  const transcriptCell = (vcon: VconSummary) => {
    const t = vcon.transcript;
    const busy = transcribing === vcon.uuid;

    const readButton = (label: string) => (
      <button type="button" title="Read what was said or written — no audio, nothing truncated"
              onClick={() => openText(vcon)}>
        {label}
      </button>
    );
    // One control per row. The language belongs in the dialog this opens:
    // three controls in a table cell made the common case — transcribe it,
    // in the language the domain is set to — look like a decision.
    const transcribeButton = (label: string) => (
      <button type="button" disabled={busy}
              title="Transcribe this recording now, instead of waiting for the nightly run"
              onClick={() => openTranscribe(vcon)}>
        {busy ? 'Transcribing…' : label}
      </button>
    );

    // Anything that is not a call recording has text but no transcription
    // lifecycle — an email, an imported transcript. Offer the reader, nothing
    // else.
    if (vcon.kind !== 'call') {
      return readButton('Read');
    }

    if (!t) {
      // No attempt yet. Whether anything is coming for it depends on the
      // deployment: the nightly run is a scheduled job that only some hosts
      // have, and saying "queued for tonight" on one without it described a
      // queue that was never going to move.
      const nightly = transcription?.nightly;
      return (
        <>
          {transcribeButton('Transcribe')}
          {nightly?.scheduled && !nightly.stale ? (
            <span className="transcript-note">
              {nightly.kind === 'automatic' ? 'transcribing shortly' : 'queued for tonight'}
            </span>
          ) : nightly?.stale ? (
            <span className="transcript-note transcript-failed"
                  title={nightly.kind === 'automatic'
                    ? `Automatic transcription last ran ${nightly.last_run}. It should run at least every 15 minutes.`
                    : `The nightly run last completed ${nightly.last_run}. It should run daily.`}>
              {nightly.kind === 'automatic' ? 'automatic transcription has stopped' : 'nightly run has stopped'}
            </span>
          ) : (
            <span className="transcript-note"
                  title="No scheduled run has ever completed on this instance — transcription here is on demand">
              not transcribed
            </span>
          )}
        </>
      );
    }

    switch (t.status) {
      case 'done':
        return (
          <>
            {readButton('📝 Read')}
            <span className="transcript-note">
              {t.confidence !== null ? `${(t.confidence * 100).toFixed(0)}% confidence` : 'transcribed'}
              {t.language && <span className="transcript-reason">{t.language}</span>}
              {t.confidence !== null && t.confidence < 0.5 && (
                <span title="The engine had low confidence. Either it could not make the audio out, or the call was not in the language it was told to expect — check the language before concluding the recording is poor."> ⚠</span>
              )}
            </span>
            {/* A transcript can be wrong rather than missing — most often
                because it was recognised as the wrong language. Re-running
                replaces it rather than stacking a second copy. */}
            {transcribeButton('Redo')}
          </>
        );
      case 'no_consent':
        // No button: it would be refused, and offering it invites someone to
        // treat consent as an obstacle rather than an answer.
        return (
          <span className="transcript-note transcript-blocked"
                title={t.consent_note || 'A party has not granted the transcription purpose'}>
            🚫 no consent
            {t.consent_note && <span className="transcript-reason">{t.consent_note}</span>}
          </span>
        );
      case 'empty':
        return (
          <>
            {transcribeButton('Retry')}
            <span className="transcript-note"
                  title="The engine recognised nothing. Silence — or the wrong language: it finds no words it is not looking for.">
              no speech found
            </span>
          </>
        );
      case 'no_audio':
        return <span className="transcript-note">no audio</span>;
      case 'failed':
        return (
          <>
            {transcribeButton('Retry')}
            <span className="transcript-note transcript-failed" title={t.error || undefined}>failed</span>
          </>
        );
      default:
        return transcribeButton('Transcribe');
    }
  };

  const openText = async (vcon: VconSummary) => {
    if (!vcon.uuid) return;
    setTextVcon(vcon);
    setTextDoc(null);
    setTextError(null);
    setTextLoading(true);
    try {
      setTextDoc(await transcriptionService.dialogText(vcon.uuid));
    } catch (err: any) {
      setTextError(err.response?.data?.error || 'Could not load the text of this vCon.');
    } finally {
      setTextLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSearchParams(prev => ({
      ...prev,
      search: searchText.trim(),
      content: contentText.trim() || undefined,
      start_from: toUtcSqlDatetime(startFromText),
      start_to: toUtcSqlDatetime(startToText),
      page: 1
    }));
  };

  const handlePageChange = (page: number) => {
    setSearchParams(prev => ({ ...prev, page }));
  };

  const formatBytes = (bytes: number) => {
    if (bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / Math.pow(1024, exp)).toFixed(exp === 0 ? 0 : 1)} ${units[exp]}`;
  };

  // Name, device address, and the user behind it. The uid is what ties a
  // recording to a person — the same user may appear under a different AOR on
  // the next call — so it is shown whenever the SRC supplied one.
  const partyLabel = (party: VconParty): string => {
    const name = party.name || '—';
    return party.tel ? `${name} <${party.tel}>` : name;
  };

  const apiErrorMessage = (err: any, fallback: string): string =>
    err?.response?.data?.error || fallback;

  const handleDownload = (vcon: VconSummary) => {
    // Attachment download served by the API; no navigation happens.
    window.location.href = vconService.downloadUrl(vcon.filename);
  };

  // Rebuilds a WAV from the audio embedded in the vCon — the recording is
  // still available in a normal audio format even when the recorder keeps
  // only the vCon.
  const handleDownloadWav = (vcon: VconSummary) => {
    window.location.href = vconService.wavFromVconUrl(vcon.filename);
  };

  const loadView = async (vcon: VconSummary, abbreviated: boolean) => {
    setViewLoading(true);
    setViewError(null);
    try {
      const doc = await vconService.view(vcon.filename, abbreviated);
      setViewDoc(doc);
      setViewAbbreviated(abbreviated);
    } catch (err) {
      console.error('Error loading vCon document:', err);
      setViewError('Failed to load the vCon document.');
    } finally {
      setViewLoading(false);
    }
  };

  const openView = (vcon: VconSummary) => {
    setViewVcon(vcon);
    setViewDoc(null);
    loadView(vcon, true);
  };

  const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file again re-triggers onChange
    // (e.g. retrying after a failed upload).
    e.target.value = '';
    if (!file) return;

    setUploadError(null);
    setUploadNotice(null);

    // PHP discards bodies over post_max_size (8M by default) — fail here
    // with a clear message instead of a server-side "empty body" error.
    if (file.size > 8 * 1024 * 1024) {
      setUploadError(`"${file.name}" is ${formatBytes(file.size)} — uploads over 8 MB are rejected by the server (post_max_size).`);
      return;
    }

    setUploadBusy(true);
    try {
      const text = await file.text();
      try {
        JSON.parse(text);
      } catch {
        setUploadError(`"${file.name}" is not valid JSON.`);
        return;
      }
      const result = await vconService.upload(file.name, text);
      setUploadNotice(`Uploaded ${result.filename}`);
      fetchVcons();
    } catch (err) {
      console.error('Error uploading vCon:', err);
      setUploadError(apiErrorMessage(err, 'Failed to upload vCon.'));
    } finally {
      setUploadBusy(false);
    }
  };

  const handleShowLifecycle = async (vcon: VconSummary) => {
    setLifecycleVcon(vcon);
    setLifecycleEvents(null);
    setLifecycleError(null);
    try {
      const response = await lifecycleService.list({ vcon_uuid: vcon.uuid || '', limit: 100 });
      const events = response.entries || [];
      // The endpoint returns newest first; a timeline reads oldest first.
      setLifecycleEvents([...events].reverse());
    } catch (err) {
      console.error('Error fetching lifecycle events:', err);
      setLifecycleError('Failed to load lifecycle events.');
    }
  };

  const fetchHolders = async (vcon: VconSummary) => {
    if (!vcon.uuid) {
      setConsentHolders([]);
      return;
    }
    try {
      setConsentHolders(await vconService.holders(vcon.uuid));
    } catch (err) {
      console.error('Error fetching copy holders:', err);
      setConsentHolders([]);
    }
  };

  const openThread = async (vcon: VconSummary) => {
    setThreadVcon(vcon);
    setThreads([]);
    setThreadSuggestions(null);
    setThreadPicked(new Set());
    setThreadIdInput('');
    setThreadConsent({});
    setThreadError(null);
    if (!vcon.uuid) { return; }
    setThreadBusy(true);
    try {
      const [t, s] = await Promise.all([
        threadService.forVcon(vcon.uuid),
        threadService.suggestions(vcon.uuid, threadWindow),
      ]);
      setThreads(t);
      setThreadSuggestions(s);
      // Segments of the same SIPREC session arrive ticked. That is not a nudge
      // toward a guess — the SBC asserted they are one call — and leaving an
      // operator to re-select them every time would make the safest link the
      // most tedious one. The inferred candidates stay unticked.
      setThreadPicked(new Set(s.filter(x => x.basis !== 'shared_party').map(x => x.uuid)));
      // Joining an existing thread is the common case, so its id is offered
      // rather than a new one minted behind the operator's back.
      if (t.length > 0) { setThreadIdInput(t[0].thread_id); }
    } catch (err) {
      setThreadError(apiErrorMessage(err, 'Failed to load threading for this conversation.'));
    } finally {
      setThreadBusy(false);
    }
  };

  const refreshThread = async (vcon: VconSummary) => {
    const [t, s] = await Promise.all([
      threadService.forVcon(vcon.uuid!),
      threadService.suggestions(vcon.uuid!, threadWindow),
    ]);
    setThreads(t);
    setThreadSuggestions(s);
    setThreadPicked(new Set());
    setThreadConsent({});
  };

  const handleThreadLink = async () => {
    if (!threadVcon?.uuid) { return; }
    setThreadBusy(true);
    setThreadError(null);
    try {
      // The conversation being viewed is always part of what it is linked to —
      // linking two others from its own modal would be a surprise.
      const uuids = [threadVcon.uuid, ...Array.from(threadPicked)];
      const result = await threadService.link(uuids, threadIdInput.trim() || undefined);
      setThreadIdInput(result.thread_id);
      await refreshThread(threadVcon);
    } catch (err) {
      setThreadError(apiErrorMessage(err, 'Failed to link these conversations.'));
    } finally {
      setThreadBusy(false);
    }
  };

  const handleThreadUnlink = async (threadId: string, uuid: string) => {
    if (!threadVcon?.uuid) { return; }
    setThreadBusy(true);
    setThreadError(null);
    try {
      await threadService.unlink(threadId, uuid);
      await refreshThread(threadVcon);
    } catch (err) {
      setThreadError(apiErrorMessage(err, 'Failed to unlink.'));
    } finally {
      setThreadBusy(false);
    }
  };

  const handleThreadConsent = async (threadId: string, purpose: string) => {
    try {
      const c = await threadService.consent(threadId, purpose);
      setThreadConsent(prev => ({ ...prev, [threadId]: c }));
    } catch (err) {
      setThreadError(apiErrorMessage(err, 'Failed to evaluate thread consent.'));
    }
  };

  const openConsent = async (vcon: VconSummary) => {
    setConsentVcon(vcon);
    setConsentDetail(null);
    setConsentError(null);
    setConsentHolders(null);
    fetchHolders(vcon);
    try {
      const detail = await consentService.get(vcon.filename);
      setConsentDetail(detail);
      const draft: Record<number, Set<string>> = {};
      detail.parties.forEach(p => { draft[p.party_index] = new Set(p.granted_purposes); });
      setConsentDraft(draft);
    } catch (err) {
      console.error('Error fetching consent:', err);
      setConsentError('Failed to load consent state.');
    }
  };

  const refreshConsent = async () => {
    if (!consentVcon) return;
    const detail = await consentService.get(consentVcon.filename);
    setConsentDetail(detail);
    const draft: Record<number, Set<string>> = {};
    detail.parties.forEach(p => { draft[p.party_index] = new Set(p.granted_purposes); });
    setConsentDraft(draft);
    fetchHolders(consentVcon);
    fetchVcons();
  };

  const applyConsent = async (partyIndex: number) => {
    if (!consentVcon) return;
    const purposes = Array.from(consentDraft[partyIndex] || []);
    if (purposes.length === 0) {
      setConsentError('Select at least one purpose to grant, or use Revoke.');
      return;
    }
    setConsentBusy(true);
    setConsentError(null);
    try {
      await consentService.set(consentVcon.filename, 'grant', partyIndex, purposes,
        (consentProofType || consentBasis) ? {
          ...(consentProofType ? { proof_type: consentProofType, proof_reference: consentProofRef } : {}),
          ...(consentBasis ? { lawful_basis: consentBasis } : {}),
        } : undefined);
      await refreshConsent();
    } catch (err) {
      setConsentError(apiErrorMessage(err, 'Failed to record consent'));
    } finally {
      setConsentBusy(false);
    }
  };

  const revokeConsent = async (partyIndex: number, label: string) => {
    if (!consentVcon) return;
    if (!window.confirm(`Revoke consent for ${label}? MCP applications will no longer be able to download or update this vCon.`)) {
      return;
    }
    setConsentBusy(true);
    setConsentError(null);
    try {
      await consentService.set(consentVcon.filename, 'revoke', partyIndex, []);
      await refreshConsent();
    } catch (err) {
      setConsentError(apiErrorMessage(err, 'Failed to revoke consent'));
    } finally {
      setConsentBusy(false);
    }
  };

  const consentBadge = (status: VconSummary['consent_status']) => {
    switch (status) {
      case 'granted': return <span className="status status-completed">granted</span>;
      case 'partial': return <span className="status status-active">partial</span>;
      case 'revoked': return <span className="status status-failed">revoked</span>;
      default: return <span className="small">none</span>;
    }
  };

  const handleRedactConfirmed = async () => {
    if (!redactTarget) return;
    setRedactBusy(true);
    setRedactError(null);
    try {
      const result = await vconService.redact(redactTarget.filename, redactParty, redactMode);
      setRedactResult(result);
      fetchVcons();
    } catch (err) {
      setRedactError(apiErrorMessage(err, 'Failed to redact party.'));
    } finally {
      setRedactBusy(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await vconService.delete(deleteTarget.filename);
      setDeleteTarget(null);
      fetchVcons();
    } catch (err) {
      console.error('Error deleting vCon:', err);
      setDeleteError(apiErrorMessage(err, 'Failed to delete vCon.'));
    } finally {
      setDeleteBusy(false);
    }
  };

  const handlePlay = async (vcon: VconSummary) => {
    setPlayback({ vcon, audioUrl: null, loading: true, error: null });
    try {
      const doc = await vconService.getVcon(vcon.filename);
      const dialog = (doc.dialog || []).find(d => d.type === 'recording' && d.body);
      if (!dialog || !dialog.body) {
        setPlayback({ vcon, audioUrl: null, loading: false, error: 'This vCon has no embedded recording.' });
        return;
      }
      if (dialog.encoding && dialog.encoding !== 'base64url') {
        setPlayback({ vcon, audioUrl: null, loading: false, error: `Unsupported dialog encoding: ${dialog.encoding}` });
        return;
      }
      const bytes = base64urlToBytes(dialog.body);
      // Browsers want "audio/wav"; the vCon spec's name for it is "audio/x-wav".
      const mimetype = dialog.mimetype === 'audio/x-wav' ? 'audio/wav' : (dialog.mimetype || 'audio/wav');
      const url = URL.createObjectURL(new Blob([bytes], { type: mimetype }));
      setPlayback({ vcon, audioUrl: url, loading: false, error: null });
    } catch (err) {
      console.error('Error loading vCon for playback:', err);
      setPlayback({ vcon, audioUrl: null, loading: false, error: 'Failed to load or decode the vCon.' });
    }
  };

  const closePlayback = () => {
    if (playback?.audioUrl) {
      URL.revokeObjectURL(playback.audioUrl);
    }
    setPlayback(null);
  };

  if (loading) return <div className="loading">Loading vCons...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="sessions-container">
      <h2>VCons</h2>

      <form onSubmit={handleSearch} className="search-form">
        <input
          name="search"
          type="text"
          placeholder="Search by UUID, Group ID, party name or number (partial match)..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
        <input
          name="content"
          type="text"
          placeholder={'Search what was said — "exact phrase", +required, -excluded'}
          title={'Full-text search over what was said: call transcripts, imported transcripts and '
            + 'email bodies. Whole words, not substrings; results are ordered by relevance. '
            + 'A call matches once it has been transcribed, which requires every party to have '
            + 'granted the transcription purpose.'}
          value={contentText}
          onChange={e => setContentText(e.target.value)}
        />
        <label className="search-time-label">
          From <input
            name="start_from"
            type="text"
            className="monospace"
            placeholder="YYYY-MM-DD HH:MM"
            title="24-hour local time, e.g. 2026-07-13 14:30"
            pattern="\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?"
            size={17}
            value={startFromText}
            onChange={e => setStartFromText(e.target.value)}
          />
        </label>
        <label className="search-time-label">
          To <input
            name="start_to"
            type="text"
            className="monospace"
            placeholder="YYYY-MM-DD HH:MM"
            title="24-hour local time, e.g. 2026-07-13 18:00"
            pattern="\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?"
            size={17}
            value={startToText}
            onChange={e => setStartToText(e.target.value)}
          />
        </label>
        <button type="submit">Search</button>
        <button
          type="button"
          onClick={() => {
            setSearchText('');
            setContentText('');
            setStartFromText('');
            setStartToText('');
            setSearchParams(prev => ({
              ...prev, search: '', content: undefined,
              start_from: undefined, start_to: undefined, page: 1,
            }));
          }}
        >
          Clear
        </button>
      </form>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <ScopeFilter
          value={{ territory: searchParams.territory, domain: searchParams.domain }}
          onChange={s => setSearchParams(prev => ({
            ...prev, territory: s.territory, domain: s.domain, page: 1,
          }))}
        />
        <button
          type="button"
          disabled={uploadBusy || readOnlyScope}
          title={readOnlyScope
            ? 'This territory is read-only; uploads target a recording territory'
            : 'Upload a vCon (.vcon.json) file'}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploadBusy ? 'Uploading…' : 'Upload vCon'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          style={{ display: 'none' }}
          onChange={handleUploadFile}
        />
        {uploadError && <span className="error">{uploadError}</span>}
        {uploadNotice && <span className="small">{uploadNotice}</span>}
      </div>

      <div className="pagination-info">
        Showing {vcons.length} of {pagination.total_count} vCons
      </div>

      <div className="table-container">
        <table className="sessions-table">
          <thead>
            <tr>
              <th>UUID</th>
              {contentSearchActive && <th title="Full-text relevance score">Rel.</th>}
              <th>Group ID</th>
              <th>Group Seq</th>
              <th>Created At</th>
              <th>Parties</th>
              <th>Consent</th>
              <th title="Transcription state and actions">Transcript</th>
              <th>Size</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {vcons.length === 0 ? (
              <tr>
                <td colSpan={contentSearchActive ? 10 : 9} className="no-data">
                  {contentSearchActive
                    ? 'No conversation matched those words. Content search covers call transcripts, '
                      + 'imported transcripts and email bodies. A call is only searchable once it has '
                      + 'been transcribed — which every party must have granted the transcription '
                      + 'purpose for. Words shorter than 3 characters and very common words are '
                      + 'never indexed.'
                    : 'No vCons found'}
                </td>
              </tr>
            ) : (
              vcons.map(vcon => (
                <React.Fragment key={vcon.filename}>
                <tr>
                  <td className="monospace small">{vcon.uuid || '—'}</td>
                  {contentSearchActive && (
                    <td className="small" style={{ textAlign: 'right' }}>
                      {vcon.relevance !== undefined ? vcon.relevance.toFixed(2) : '—'}
                    </td>
                  )}
                  <td className="monospace small">{vcon.group_id || '—'}</td>
                  <td>{vcon.group_seq ?? '—'}</td>
                  <td>{vcon.created_at ? formatDateTime(vcon.created_at) : '—'}</td>
                  <td className="small">
                    {vcon.parties.length === 0
                      ? '—'
                      : vcon.parties.map((party, i) => (
                          <div key={i}>
                            {party.role ? `${party.role}: ` : ''}{partyLabel(party)}
                            {party.uid && (
                              <div className="monospace small party-uid"
                                   title="NetSapiens user ID — identifies the person across all their devices">
                                uid: {party.uid}
                              </div>
                            )}
                          </div>
                        ))}
                  </td>
                  <td>
                    {consentBadge(vcon.consent_status)}
                  </td>
                  <td className="transcript-cell">{transcriptCell(vcon)}</td>
                  <td>{formatBytes(vcon.size_bytes)}</td>
                  <td className="actions-cell">
                    <button
                      type="button"
                      title="View or record consent for this vCon"
                      onClick={() => openConsent(vcon)}
                    >
                      Consent
                    </button>
                    <button
                      type="button"
                      title="View the vCon document (media abbreviated by default)"
                      onClick={() => openView(vcon)}
                    >
                      View
                    </button>
                    <button
                      type="button"
                      title="Download this vCon as a text file"
                      onClick={() => handleDownload(vcon)}
                    >
                      Download
                    </button>
                    {/* Audio actions only where there is audio — i.e. real
                        recordings. An imported email thread or an experimental
                        transcript has a text dialog and no recording, so WAV
                        and Play would download an empty file and open a player
                        with nothing in it. */}
                    {vcon.kind === 'call' && (
                      <>
                        <button
                          type="button"
                          title="Download the call audio as a WAV, rebuilt from this vCon"
                          onClick={() => handleDownloadWav(vcon)}
                        >
                          WAV
                        </button>
                        <button
                          type="button"
                          title="Play the audio embedded in this vCon"
                          onClick={() => handlePlay(vcon)}
                        >
                          Play
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      disabled={!vcon.uuid}
                      title="Show this vCon's lifecycle audit trail"
                      onClick={() => handleShowLifecycle(vcon)}
                    >
                      Lifecycle
                    </button>
                    <button
                      type="button"
                      disabled={!vcon.uuid}
                      title={vcon.threads && vcon.threads.length > 0
                        ? `In ${vcon.threads.length === 1
                            ? `an interaction of ${vcon.threads[0].members} conversations`
                            : `${vcon.threads.length} interactions`}`
                        : 'Link this conversation with others that are part of the same interaction'}
                      onClick={() => openThread(vcon)}
                    >
                      {vcon.threads && vcon.threads.length > 0
                        ? `Thread (${vcon.threads.length === 1
                            ? vcon.threads[0].members
                            : vcon.threads.length + '\u00d7'})`
                        : 'Thread'}
                    </button>
                    <button
                      type="button"
                      className="danger"
                      disabled={vcon.parties.length === 0}
                      title="Remove one party's identity and voice from this vCon (asks for confirmation)"
                      onClick={() => { setRedactError(null); setRedactResult(null); setRedactParty(0); setRedactTarget(vcon); }}
                    >
                      Redact
                    </button>
                    <button
                      type="button"
                      className="danger"
                      title="Delete this vCon (asks for confirmation)"
                      onClick={() => { setDeleteError(null); setDeleteTarget(vcon); }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
                {transcribeResult?.uuid === vcon.uuid && (
                  <tr>
                    <td colSpan={contentSearchActive ? 9 : 8} className="small"
                        style={{ paddingLeft: '28px', color: transcribeResult.ok ? undefined : '#c0392b' }}>
                      {transcribeResult.ok ? '✓ ' : '⚠ '}{transcribeResult.text}
                    </td>
                  </tr>
                )}
                {contentSearchActive && vcon.snippet && (
                  <tr>
                    <td colSpan={10} className="small vcon-snippet">
                      {vcon.snippet}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.total_pages > 1 && (
        <div className="pagination">
          <button
            disabled={!pagination.has_prev}
            onClick={() => handlePageChange(pagination.current_page - 1)}
          >
            Previous
          </button>

          <span className="page-info">
            Page {pagination.current_page} of {pagination.total_pages}
          </span>

          <button
            disabled={!pagination.has_next}
            onClick={() => handlePageChange(pagination.current_page + 1)}
          >
            Next
          </button>
        </div>
      )}

      {textVcon && (
        <div className="modal-overlay" onClick={() => setTextVcon(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{textDoc?.kind === 'call' ? 'Transcript' : 'Text'}</h3>
              <button type="button" onClick={() => setTextVcon(null)}>Close</button>
            </div>
            <div className="modal-subtitle monospace small">{textVcon.uuid || textVcon.filename}</div>
            {textDoc && (
              <div className="modal-subtitle small">
                {textDoc.subject ? <><strong>{textDoc.subject}</strong> · </> : null}
                {textDoc.parties.join(', ')} · {textDoc.text_chars.toLocaleString()} characters
              </div>
            )}
            {textLoading && <div className="loading">Loading…</div>}
            {textError && <div className="error" style={{ marginTop: '0.75rem' }}>{textError}</div>}
            {textDoc && (
              <div className="modal-body">
                {textDoc.segments.filter(s => s.text || s.audio_omitted).length === 0 && (
                  <p className="small">This vCon carries no readable text.</p>
                )}
                {(() => {
                  const turns = conversationTurns(textDoc.segments);
                  if (!turns) return null;
                  return (
                    <>
                      {textDoc.segments.filter(s => s.audio_omitted).map(seg => (
                        <p key={seg.index} className="small" style={{ opacity: 0.6, fontStyle: 'italic' }}>
                          🔊 {seg.duration ? `${Math.round(seg.duration)}s of audio` : 'Audio'}
                          {' '}— one channel per participant; use Play to listen.
                        </p>
                      ))}
                      <p className="small" style={{ opacity: 0.7 }}>
                        Machine transcript, each participant recognised from their own
                        audio channel and placed by when they spoke.
                      </p>
                      {turns.map(turn => (
                        <div key={turn.key} className="transcript-turn"
                             style={{ borderLeftColor: SPEAKER_COLORS[turn.colour % SPEAKER_COLORS.length] }}>
                          <div className="small transcript-turn-head">
                            <span style={{ fontWeight: 600 }}>{turn.speaker}</span>
                            <span className="monospace" style={{ opacity: 0.7 }}>
                              {' '}{formatOffset(turn.start)}{turn.end != null && `–${formatOffset(turn.end)}`}
                            </span>
                            {turn.confidence != null && (
                              <span style={{ opacity: 0.7 }}>
                                {' '}· {(turn.confidence * 100).toFixed(0)}%{turn.confidence < 0.5 && ' ⚠'}
                              </span>
                            )}
                            {turn.overlaps && (
                              <span style={{ opacity: 0.7 }} title="Began while someone else was still speaking"> · overlapping</span>
                            )}
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{turn.text}</div>
                        </div>
                      ))}
                    </>
                  );
                })()}
                {!conversationTurns(textDoc.segments) && textDoc.segments.map(seg => {
                  if (seg.audio_omitted) {
                    return (
                      <p key={seg.index} className="small" style={{ opacity: 0.6, fontStyle: 'italic' }}>
                        🔊 {seg.duration ? `${Math.round(seg.duration)}s of audio` : 'Audio'}
                        {seg.speaker ? ` from ${seg.speaker}` : ''} — not shown here; use Play.
                      </p>
                    );
                  }
                  if (!seg.text) return null;
                  const conf = seg.generated_by?.confidence;
                  return (
                    <div key={seg.index} style={{ marginBottom: '14px' }}>
                      <div className="small" style={{ fontWeight: 600, marginBottom: '2px' }}>
                        {seg.speaker || 'Unattributed'}
                        {seg.is_transcript && (
                          <span style={{ fontWeight: 400, opacity: 0.7 }}>
                            {' '}· machine transcript
                            {conf != null && ` · ${(conf * 100).toFixed(0)}% confidence`}
                            {conf != null && conf < 0.5 && ' ⚠'}
                          </span>
                        )}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{seg.text}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {viewVcon && (
        <div className="modal-overlay" onClick={() => setViewVcon(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>vCon document</h3>
              <button type="button" onClick={() => setViewVcon(null)}>Close</button>
            </div>
            <div className="modal-subtitle monospace small">{viewVcon.uuid || viewVcon.filename}</div>
            <div className="modal-subtitle small" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {viewAbbreviated
                ? 'Inline media abbreviated for size — fields marked body_truncated.'
                : `Full document (${formatBytes(viewVcon.size_bytes)}).`}
              <button
                type="button"
                disabled={viewLoading}
                title={viewAbbreviated
                  ? `Load the complete document including embedded media (${formatBytes(viewVcon.size_bytes)})`
                  : 'Back to the abbreviated view'}
                onClick={() => loadView(viewVcon, !viewAbbreviated)}
              >
                {viewLoading
                  ? 'Loading…'
                  : viewAbbreviated
                    ? `Show full vCon (${formatBytes(viewVcon.size_bytes)})`
                    : 'Show abbreviated'}
              </button>
            </div>
            {viewError && <div className="error" style={{ marginTop: '0.75rem' }}>{viewError}</div>}
            {!viewDoc && !viewError && (
              <div className="small" style={{ marginTop: '0.75rem' }}>Loading vCon document…</div>
            )}
            {viewDoc && (
              <pre className="modal-xml">{JSON.stringify(viewDoc, null, 2)}</pre>
            )}
          </div>
        </div>
      )}

      {consentVcon && (
        <div className="modal-overlay" onClick={() => !consentBusy && setConsentVcon(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Consent</h3>
              <button type="button" disabled={consentBusy} onClick={() => setConsentVcon(null)}>Close</button>
            </div>
            <div className="modal-subtitle monospace small">{consentVcon.uuid || consentVcon.filename}</div>
            {consentError && <div className="error" style={{ marginTop: '0.75rem' }}>{consentError}</div>}
            {!consentDetail && !consentError && (
              <div className="small" style={{ marginTop: '0.75rem' }}>Loading consent state…</div>
            )}
            {consentDetail && (
              <>
                <div className="modal-subtitle small" style={{ marginTop: '0.5rem' }}>
                  Overall: {consentBadge(consentDetail.overall)}{' '}
                  <span className="small">
                    — recorder-created vCons start with no consent; only an explicit revocation
                    blocks MCP access.
                  </span>
                </div>
                <div className="small" style={{ marginTop: '0.5rem', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span title="WHY the processing is lawful (GDPR Article 6). Leave unchanged unless this grant is what establishes the grounds — a console grant usually is not.">
                    Lawful basis:
                  </span>
                  <select value={consentBasis} onChange={e => setConsentBasis(e.target.value)}>
                    <option value="">leave unchanged</option>
                    {CONSENT_LAWFUL_BASES.map(b => (
                      <option key={b} value={b}>{b} — {LAWFUL_BASIS_LABELS[b]}</option>
                    ))}
                  </select>
                </div>
                <div className="small" style={{ marginTop: '0.35rem', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span title="HOW the basis was established, and what can be shown for it. Separate from the basis itself.">
                    Proof attached to grants:
                  </span>
                  <select value={consentProofType} onChange={e => setConsentProofType(e.target.value)}>
                    <option value="">none (bare assertion)</option>
                    {CONSENT_PROOF_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {consentProofType && (
                    <input type="text" placeholder="evidence reference" size={22} value={consentProofRef}
                      onChange={e => setConsentProofRef(e.target.value)} />
                  )}
                </div>
                <div className="table-container" style={{ marginTop: '0.75rem' }}>
                  <table className="sessions-table">
                    <thead>
                      <tr>
                        <th>Party</th>
                        <th>Purposes</th>
                        <th>Basis</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consentDetail.parties.map(party => {
                        const label = party.party_name || party.party_tel || `party ${party.party_index}`;
                        const revoked = party.granted_purposes.length === 0 && party.revoked_purposes.length > 0;
                        return (
                          <tr key={party.party_index}>
                            <td className="small">
                              {party.party_name || '—'}
                              {party.party_uid && (
                                <div className="monospace small" title="NetSapiens user ID">
                                  uid: {party.party_uid}
                                </div>
                              )}
                              <div className="monospace small" style={{ opacity: 0.7 }}>{party.party_tel || ''}</div>
                            </td>
                            <td className="small">
                              {consentDetail.purposes.map(purpose => (
                                <label key={purpose} style={{ display: 'block', whiteSpace: 'nowrap' }}>
                                  <input
                                    type="checkbox"
                                    disabled={consentBusy}
                                    checked={consentDraft[party.party_index]?.has(purpose) || false}
                                    onChange={e => {
                                      setConsentDraft(prev => {
                                        const next = { ...prev };
                                        const set = new Set(next[party.party_index] || []);
                                        if (e.target.checked) set.add(purpose); else set.delete(purpose);
                                        next[party.party_index] = set;
                                        return next;
                                      });
                                    }}
                                  /> {purpose}
                                </label>
                              ))}
                            </td>
                            <td className="small">
                              <span className="monospace" title={LAWFUL_BASIS_LABELS[party.lawful_basis] || undefined}>
                                {party.lawful_basis}
                              </span>
                              <div style={{ opacity: 0.7 }}>
                                {LAWFUL_BASIS_LABELS[party.lawful_basis] || ''}
                              </div>
                            </td>
                            <td className="small">
                              {revoked ? (
                                <>
                                  <span className="status status-failed">revoked</span>
                                  <div>{party.revoked_at ? formatDateTime(party.revoked_at) : ''}</div>
                                  <div>{party.revoked_by ? `by ${party.revoked_by}` : ''}</div>
                                </>
                              ) : party.granted_purposes.length > 0 ? (
                                <>
                                  <span className="status status-completed">granted</span>
                                  <div>{party.granted_at ? formatDateTime(party.granted_at) : ''}</div>
                                  <div>{party.granted_by ? `by ${party.granted_by}` : ''}</div>
                                  {party.proof_type && (
                                    <div title={party.proof_reference || 'no reference recorded'}>
                                      proof: {party.proof_type}
                                      {party.proof_reference ? ` (${party.proof_reference.length > 24 ? party.proof_reference.slice(0, 24) + '…' : party.proof_reference})` : ''}
                                    </div>
                                  )}
                                  {party.expires_at && (
                                    <div title="The disposition job revokes this grant once the expiry passes">
                                      expires {formatDateTime(party.expires_at)}
                                    </div>
                                  )}
                                </>
                              ) : (
                                'none'
                              )}
                            </td>
                            <td>
                              <button type="button" disabled={consentBusy}
                                title="Record consent for the checked purposes"
                                onClick={() => applyConsent(party.party_index)}>
                                Grant
                              </button>
                              <button type="button" className="danger" disabled={consentBusy}
                                title="Revoke all consent for this party"
                                onClick={() => revokeConsent(party.party_index, label)}>
                                Revoke
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <h4 style={{ marginTop: '1rem', marginBottom: '0.25rem' }}>Copies &amp; notifications</h4>
                <div className="small">
                  Applications that downloaded this vCon over MCP, and where each stands in the
                  revocation cascade (notified on revoke; purge confirmed via acknowledge_purge).
                </div>
                {consentHolders === null ? (
                  <div className="small" style={{ marginTop: '0.5rem' }}>Loading copy holders…</div>
                ) : consentHolders.length === 0 ? (
                  <div className="small" style={{ marginTop: '0.5rem' }}>
                    No external application holds a copy of this vCon.
                  </div>
                ) : (
                  <div className="table-container" style={{ marginTop: '0.5rem' }}>
                    <table className="sessions-table">
                      <thead>
                        <tr>
                          <th>Application</th>
                          <th>Last copy sent</th>
                          <th>Cascade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {consentHolders.map(holder => (
                          <tr key={holder.app_name}>
                            <td className="monospace small">{holder.app_name}</td>
                            <td className="small">{holder.last_sent_at ? formatDateTime(holder.last_sent_at) : '—'}</td>
                            <td className="small">
                              {holder.notice_status === 'purged' ? (
                                <>
                                  <span className="status status-completed">purged</span>
                                  <div>{holder.purged_at ? formatDateTime(holder.purged_at) : ''}</div>
                                </>
                              ) : holder.notice_status === 'pending' ? (
                                <>
                                  <span className="status status-active">notified</span>
                                  <div>{holder.notified_at ? formatDateTime(holder.notified_at) : ''}</div>
                                  <div className="small">awaiting purge confirmation</div>
                                </>
                              ) : (
                                <span title="No revocation has been recorded, so no purge obligation exists">
                                  holds copy
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Transcribe: the language choice, and what it is about to assume if
          nobody makes one. Recognition is told the language rather than
          detecting it, so this dialog exists to make the assumption visible at
          the moment it is acted on — a wrong language does not fail, it
          returns the few words of English it can force-fit onto the audio. */}
      {transcribeTarget && (() => {
        const target = transcribeTarget;
        const busy = transcribing === target.uuid;
        const fallback = domainDefaultFor(target);
        const previous = target.transcript;
        const chosen = transcribeLanguage
          ? transcription?.languages?.find(l => l.code === transcribeLanguage)
          : undefined;
        const effective = transcribeLanguage || fallback?.language || transcription?.default_language;
        const effectiveModel = chosen?.model ?? fallback?.model;
        return (
          <div className="modal-overlay" onClick={() => !busy && setTranscribeTarget(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{previous?.status === 'done' ? 'Transcribe again' : 'Transcribe recording'}</h3>
                <button type="button" disabled={busy} onClick={() => setTranscribeTarget(null)}>Close</button>
              </div>
              <div className="modal-subtitle small">
                {target.parties.map(partyLabel).join(' → ') || target.filename}
              </div>
              <div className="modal-subtitle monospace small">{target.uuid || target.filename}</div>

              {transcribeError && <div className="error" style={{ marginTop: '0.75rem' }}>{transcribeError}</div>}

              {previous?.status === 'done' && (
                <p className="small" style={{ marginTop: '0.75rem' }}>
                  Already transcribed
                  {previous.language ? ` as ${previous.language}` : ''}
                  {previous.confidence != null
                    ? `, at ${(previous.confidence * 100).toFixed(0)}% confidence` : ''}.
                  Running again <strong>replaces</strong> that transcript rather than adding a second
                  one{previous.confidence != null && previous.confidence < 0.5
                    ? ' — a score that low is often the wrong language rather than poor audio.'
                    : '.'}
                </p>
              )}

              <p className="small" style={{ marginTop: '0.75rem' }}>
                Recognition has to be told what language to expect; it does not work it
                out. Asked for the wrong one it does not fail — it returns the handful of
                words it can force-fit, at low confidence.
              </p>

              <label className="small" style={{ display: 'block', marginTop: '0.75rem' }}>
                Language
                <select value={transcribeLanguage} disabled={busy}
                        style={{ display: 'block', marginTop: '4px', minWidth: '320px' }}
                        onChange={e => setTranscribeLanguage(e.target.value)}>
                  <option value="">
                    {fallback
                      ? `This domain's setting — ${fallback.language}`
                      : `Deployment default — ${transcription?.default_language ?? 'en-US'}`}
                  </option>
                  {(transcription?.languages ?? []).map(l => (
                    <option key={l.code} value={l.code}>{l.label} — {l.code}</option>
                  ))}
                </select>
              </label>

              <p className="small" style={{ marginTop: '0.5rem', opacity: 0.85 }}>
                Will be recognised as <span className="monospace">{effective}</span>
                {effectiveModel && <> with the <span className="monospace">{effectiveModel}</span> model</>}
                {effectiveModel === 'default' && (
                  <> — no telephony-trained model exists for this language, so expect lower
                     confidence on call audio however good the recording</>
                )}
                . Billed per non-silent channel, and it takes a few seconds to a minute.
              </p>

              <div style={{ marginTop: '0.75rem' }}>
                <button type="button" disabled={busy} onClick={() => handleTranscribe(target)}>
                  {busy ? 'Transcribing…' : previous?.status === 'done' ? 'Transcribe again' : 'Transcribe'}
                </button>
                <button type="button" disabled={busy} onClick={() => setTranscribeTarget(null)}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {redactTarget && (
        <div className="modal-overlay" onClick={() => !redactBusy && setRedactTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Redact a party</h3>
              <button type="button" disabled={redactBusy} onClick={() => setRedactTarget(null)}>Close</button>
            </div>
            <div className="modal-subtitle monospace small">{redactTarget.uuid || redactTarget.filename}</div>
            {redactError && <div className="error" style={{ marginTop: '0.75rem' }}>{redactError}</div>}
            {redactResult ? (
              <div style={{ marginTop: '0.75rem' }} className="small">
                <p>
                  Party {redactResult.party} has been redacted: identity replaced with a marker
                  {redactResult.audio_silenced
                    ? ', their audio channel silenced'
                    : `; audio NOT silenced (${redactResult.audio_skip_reason || 'no embedded stereo audio'})`}
                  {redactResult.mode === 'erase' && redactResult.wav_redacted
                    ? ', and the source WAV silenced to match.' : '.'}
                </p>
                {redactResult.mode === 'derive' ? (
                  <>
                    <p>
                      Written as a new conversation, <span className="monospace">{redactResult.derived_uuid}</span>,
                      which references this one as the version it was redacted from. This
                      conversation is unchanged, and applications reaching it through the API are
                      now sent to the redacted copy instead.
                    </p>
                    <p>Nothing was destroyed, so this can be undone by deleting the copy.</p>
                  </>
                ) : (
                  <p>
                    The conversation, its other parties, and the audit trail survive — redaction is
                    not deletion — but this party's identity and voice are gone for good.
                  </p>
                )}
              </div>
            ) : (
              <>
                <p className="small" style={{ marginTop: '0.75rem' }}>
                  Redaction removes one party's identity — name and number become a marker,
                  including inside the SIPREC metadata — and silences their side of the stereo
                  audio. Consent state and the lifecycle ledger survive either way.
                </p>
                <div className="small" style={{ marginTop: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.4rem' }}>
                    <input
                      type="radio"
                      name="redact-mode"
                      checked={redactMode === 'derive'}
                      disabled={redactBusy}
                      onChange={() => setRedactMode('derive')}
                    /> <strong>Keep the original, write a redacted copy</strong>
                    <div style={{ marginLeft: '1.4rem', opacity: 0.8 }}>
                      A new conversation that references this one as the version it was redacted
                      from. This one stays intact and becomes restricted: applications reaching it
                      through the API are sent to the copy. Reversible — for a party who declined
                      one purpose while the others agreed.
                    </div>
                  </label>
                  <label style={{ display: 'block' }}>
                    <input
                      type="radio"
                      name="redact-mode"
                      checked={redactMode === 'erase'}
                      disabled={redactBusy}
                      onChange={() => setRedactMode('erase')}
                    /> <strong>Erase in place</strong>
                    <div style={{ marginLeft: '1.4rem', opacity: 0.8 }}>
                      Rewrites this conversation and its source WAV. Nothing is recoverable
                      afterwards. For a right-to-erasure request, and nothing else.
                    </div>
                  </label>
                </div>
                <div className="small" style={{ marginTop: '0.5rem' }}>
                  {redactTarget.parties.map((party, i) => (
                    <label key={i} style={{ display: 'block' }}>
                      <input
                        type="radio"
                        name="redact-party"
                        checked={redactParty === i}
                        disabled={redactBusy}
                        onChange={() => setRedactParty(i)}
                      /> {party.role ? `${party.role}: ` : ''}{partyLabel(party)}
                    </label>
                  ))}
                </div>
                <div style={{ marginTop: '0.75rem' }}>
                  <button type="button" className={redactMode === 'erase' ? 'danger' : ''}
                    disabled={redactBusy} onClick={handleRedactConfirmed}>
                    {redactBusy
                      ? 'Redacting…'
                      : redactMode === 'erase' ? 'Erase this party, permanently' : 'Write a redacted copy'}
                  </button>
                  <button type="button" disabled={redactBusy} onClick={() => setRedactTarget(null)}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {threadVcon && (
        <div className="modal-overlay" onClick={() => !threadBusy && setThreadVcon(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Thread</h3>
              <button type="button" disabled={threadBusy} onClick={() => setThreadVcon(null)}>Close</button>
            </div>
            <div className="modal-subtitle monospace small">{threadVcon.uuid || threadVcon.filename}</div>
            <div className="modal-subtitle small">
              One interaction can span a call, a text and an email. Linking records that in the
              conversations themselves, so it travels with them.
            </div>
            {threadError && <div className="error" style={{ marginTop: '0.75rem' }}>{threadError}</div>}

            {/* --- what it is already part of --- */}
            {threads.length > 0 ? threads.map(t => (
              <div key={t.thread_id} style={{ marginTop: '1rem' }}>
                <div className="small">
                  <strong>In thread</strong> <span className="monospace">{t.thread_id}</span>
                  {' '}— {t.members.length} conversation{t.members.length === 1 ? '' : 's'}
                </div>
                <div className="table-container" style={{ marginTop: '0.4rem' }}>
                  <table className="sessions-table">
                    <thead>
                      <tr>
                        <th>When</th><th>Kind</th><th>Parties</th>
                        <th title="Which SIPREC recording session this segment belongs to">Session</th>
                        <th title="Its position within that session (group_seq)">Seq</th>
                        <th title="What the SBC called the state change (byAction)">Action</th>
                        <th title="The session this one was transferred from (xferredGroupID)">From</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const letters = sessionLetters(t.members);
                        return t.members.map(m => (
                        <tr key={m.vcon_uuid}
                            style={m.vcon_uuid === threadVcon.uuid ? { fontWeight: 600 } : undefined}>
                          <td className="small">{formatDateTime(m.started_at)}</td>
                          <td className="small">{m.kind}</td>
                          <td className="small">
                            {m.parties.map(p => partyLabel(p)).join(', ') || '—'}
                            {m.vcon_uuid === threadVcon.uuid && <span className="small"> (this one)</span>}
                          </td>
                          <td className="small monospace" title={m.group_id || 'no SIPREC session'}>
                            {m.group_id ? letters[m.group_id] : '—'}
                          </td>
                          <td className="small monospace">{m.group_seq ?? '—'}</td>
                          <td className="small">{m.by_action || '—'}</td>
                          <td className="small monospace" title={m.xferred_group_id || 'not a transfer'}>
                            {m.xferred_group_id
                              ? (letters[m.xferred_group_id] || m.xferred_group_id.slice(0, 8))
                              : '—'}
                          </td>
                          <td>
                            <button type="button" disabled={threadBusy}
                              title="Remove this conversation from the thread"
                              onClick={() => handleThreadUnlink(t.thread_id, m.vcon_uuid)}>
                              Unlink
                            </button>
                          </td>
                        </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>

                <SessionLegend members={t.members} />

                {/* Asked for, not volunteered: a thread-wide answer is the most
                    restrictive member's, so showing it beside the per-conversation
                    state would invite reading it as that. */}
                <div className="small" style={{ marginTop: '0.5rem', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span title="A thread-wide answer takes the most restrictive member's — one conversation whose parties declined is decisive">
                    May the whole thread be used for:
                  </span>
                  {['transcription', 'analysis', 'ai_training'].map(p => (
                    <button key={p} type="button" disabled={threadBusy}
                      onClick={() => handleThreadConsent(t.thread_id, p)}>{p}</button>
                  ))}
                </div>
                {threadConsent[t.thread_id] && (
                  <div className="small" style={{ marginTop: '0.4rem' }}>
                    <span className={threadConsent[t.thread_id].allowed ? 'status status-completed' : 'status status-failed'}>
                      {threadConsent[t.thread_id].allowed ? 'permitted' : 'not permitted'}
                    </span>
                    {' '}for <span className="monospace">{threadConsent[t.thread_id].purpose}</span>
                    {' '}across {threadConsent[t.thread_id].members} conversation
                    {threadConsent[t.thread_id].members === 1 ? '' : 's'}
                    {threadConsent[t.thread_id].blocked_by.length > 0 && (
                      <div style={{ opacity: 0.85, marginTop: '0.2rem' }}>
                        Blocked by {threadConsent[t.thread_id].blocked_by.length} of them:{' '}
                        {threadConsent[t.thread_id].blocked_by.map(b => (
                          <span key={b.uuid} className="monospace" title={b.uuid}>
                            {b.uuid.slice(0, 8)}… ({b.status}){' '}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )) : (
              <p className="small" style={{ marginTop: '1rem' }}>
                Not part of any thread yet.
              </p>
            )}

            {/* --- what might belong with it --- */}
            <div style={{ marginTop: '1.25rem' }}>
              <div className="small" style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                <strong>Might belong with this</strong>
                <span style={{ opacity: 0.8 }}>— same call, or shares a participant within</span>
                <select value={threadWindow} disabled={threadBusy}
                  onChange={async e => {
                    const days = parseInt(e.target.value, 10);
                    setThreadWindow(days);
                    if (threadVcon.uuid) {
                      const next = await threadService.suggestions(threadVcon.uuid, days);
                      setThreadSuggestions(next);
                      setThreadPicked(prev => new Set([
                        ...Array.from(prev).filter(u => next.some(x => x.uuid === u)),
                        ...next.filter(x => x.basis !== 'shared_party').map(x => x.uuid),
                      ]));
                    }
                  }}>
                  {[1, 3, 7, 14, 30].map(d => <option key={d} value={d}>{d} day{d === 1 ? '' : 's'}</option>)}
                </select>
              </div>
              <p className="small" style={{ opacity: 0.8, marginTop: '0.3rem' }}>
                <strong>Same call</strong> and <strong>transfer leg</strong> mean the recorder was
                told so, not that it guessed. Same call is one SIPREC session — a blind transfer or
                a second leg — sharing a group id. A supervised transfer has three segments and
                only two share that group: the consultation is a separate session, reachable only
                through the xferredGroupID the post-transfer segment carries. Both arrive ticked.{' '}
                <strong>Shares a participant</strong> is an inference and nothing more: two people
                who spoke twice had two conversations, and nothing here can tell that from a call
                and its own follow-up.
              </p>

              {threadSuggestions === null ? (
                <p className="small">Loading…</p>
              ) : threadSuggestions.length === 0 ? (
                <p className="small">
                  Nothing else shares a participant with this conversation in that window.
                </p>
              ) : (
                <div className="table-container">
                  <table className="sessions-table">
                    <thead>
                      <tr><th></th><th>Why</th><th>When</th><th>Kind</th><th>Apart</th><th>Parties</th></tr>
                    </thead>
                    <tbody>
                      {threadSuggestions.map(sg => (
                        <tr key={sg.uuid}>
                          <td>
                            <input type="checkbox" disabled={threadBusy}
                              checked={threadPicked.has(sg.uuid)}
                              onChange={e => setThreadPicked(prev => {
                                const next = new Set(prev);
                                if (e.target.checked) { next.add(sg.uuid); } else { next.delete(sg.uuid); }
                                return next;
                              })} />
                          </td>
                          <td className="small">
                            {sg.basis === 'siprec_group' ? (
                              <span className="status status-completed"
                                title={`One SIPREC session — group ${sg.group_id}, segment ${sg.group_seq}`
                                  + (sg.by_action ? ` (${sg.by_action})` : '')}>
                                same call
                              </span>
                            ) : sg.basis === 'siprec_xfer' ? (
                              <span className="status status-completed"
                                title={`Consultation leg of a supervised transfer — a separate SIPREC `
                                  + `session (${sg.group_id}) linked by xferredGroupID`
                                  + (sg.by_action ? `, byAction ${sg.by_action}` : '')}>
                                transfer leg
                              </span>
                            ) : (
                              <span title="Inferred: the same person appears in both, close in time">
                                {sg.shared_parties} shared part{sg.shared_parties === 1 ? 'y' : 'ies'}
                              </span>
                            )}
                          </td>
                          <td className="small">{formatDateTime(sg.created_at)}</td>
                          <td className="small">{sg.kind}</td>
                          <td className="small">{sg.hours_apart}h</td>
                          <td className="small">{sg.parties.map(p => partyLabel(p)).join(', ') || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="small" style={{ marginTop: '0.6rem', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span title="Free-form: a CRM case number is a better label than a minted id. Leave blank to mint one.">
                  Thread id:
                </span>
                <input type="text" size={26} value={threadIdInput} disabled={threadBusy}
                  placeholder="leave blank to mint one"
                  onChange={e => setThreadIdInput(e.target.value)} />
                <button type="button" disabled={threadBusy || threadPicked.size === 0}
                  onClick={handleThreadLink}>
                  {threadBusy ? 'Linking…' :
                    `Link ${threadPicked.size} selected with this one`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {lifecycleVcon && (
        <div className="modal-overlay" onClick={() => setLifecycleVcon(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>vCon lifecycle</h3>
              <button type="button" onClick={() => setLifecycleVcon(null)}>Close</button>
            </div>
            <div className="modal-subtitle monospace small">{lifecycleVcon.uuid}</div>
            <div className="modal-subtitle small">
              {lifecycleVcon.parties.map(partyLabel).join(' → ') || '—'}
            </div>
            {lifecycleError && <div className="error" style={{ marginTop: '0.75rem' }}>{lifecycleError}</div>}
            {!lifecycleEvents && !lifecycleError && (
              <div className="small" style={{ marginTop: '0.75rem' }}>Loading lifecycle events…</div>
            )}
            {lifecycleEvents && lifecycleEvents.length === 0 && (
              <div className="small" style={{ marginTop: '0.75rem' }}>
                No lifecycle events recorded for this vCon (it predates the ledger).
              </div>
            )}
            {lifecycleEvents && lifecycleEvents.length > 0 && (
              <div className="table-container" style={{ marginTop: '0.75rem' }}>
                <table className="sessions-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Event</th>
                      <th>Actor</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lifecycleEvents.map(event => (
                      <tr key={event.id}>
                        <td className="small">{formatDateTime(event.created_at)}</td>
                        <td>{LIFECYCLE_EVENT_LABELS[event.event] || event.event}</td>
                        <td className="small">
                          {lifecycleActorLabel(event)}
                          {event.source_ip && <div className="monospace">{event.source_ip}</div>}
                        </td>
                        <td className="monospace small" title={event.detail || undefined}>
                          {event.detail
                            ? (event.detail.length > 50 ? event.detail.slice(0, 50) + '…' : event.detail)
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="modal-overlay" onClick={() => !deleteBusy && setDeleteTarget(null)}>
          <div className="modal modal-narrow" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete vCon?</h3>
              <button type="button" disabled={deleteBusy} onClick={() => setDeleteTarget(null)}>Close</button>
            </div>
            <div className="modal-subtitle monospace small">{deleteTarget.uuid || deleteTarget.filename}</div>
            <div className="modal-subtitle small">
              {deleteTarget.parties.map(partyLabel).join(' → ') || '—'}
              {deleteTarget.created_at ? ` · ${formatDateTime(deleteTarget.created_at)}` : ''}
              {` · ${formatBytes(deleteTarget.size_bytes)}`}
            </div>
            <div className="small" style={{ marginTop: '0.75rem' }}>
              This permanently deletes <span className="monospace">{deleteTarget.filename}</span> from
              the server. The WAV recording (if any) is not affected. This cannot be undone.
            </div>
            {deleteError && <div className="error" style={{ marginTop: '0.75rem' }}>{deleteError}</div>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '1rem' }}>
              <button type="button" className="danger" disabled={deleteBusy} onClick={handleDeleteConfirmed}>
                {deleteBusy ? 'Deleting…' : 'Yes, delete'}
              </button>
              <button type="button" disabled={deleteBusy} onClick={() => setDeleteTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {playback && (
        <div className="modal-overlay" onClick={closePlayback}>
          <div className="modal modal-narrow" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>vCon playback</h3>
              <button type="button" onClick={closePlayback}>Close</button>
            </div>
            <div className="modal-subtitle monospace small">{playback.vcon.uuid || playback.vcon.filename}</div>
            <div className="modal-subtitle small">
              {playback.vcon.parties.map(partyLabel).join(' → ') || '—'}
              {playback.vcon.created_at ? ` · ${formatDateTime(playback.vcon.created_at)}` : ''}
            </div>
            {playback.vcon.consent_status === 'revoked' && (
              <div className="error" style={{ marginTop: '0.75rem' }}>
                ⚠ Consent for this conversation has been revoked. As Data Controller you can still
                review it, but treat this recording accordingly.
              </div>
            )}
            {playback.loading && (
              <div className="small" style={{ marginTop: '0.75rem' }}>Loading vCon and decoding embedded audio…</div>
            )}
            {playback.error && (
              <div className="error" style={{ marginTop: '0.75rem' }}>{playback.error}</div>
            )}
            {playback.audioUrl && (
              <audio
                controls
                autoPlay
                style={{ width: '100%', marginTop: '0.75rem' }}
                src={playback.audioUrl}
              >
                Your browser does not support audio playback.
              </audio>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VconsTable;
